import {
  GLOBAL_LISTING_TYPE_OPTIONS,
  GLOBAL_PROPERTY_TYPE_OPTIONS,
  GLOBAL_TIME_RANGE_OPTIONS,
  type JobConfigFromPromptResult,
  type JobCrawlConfig,
} from "@repo/adapter-definitions/job-config";
import type { CrawlJobConfig } from "@/lib/api/types";

/** Arguments for createQueueJob, shared by the wizard and "Describe the job". */
export interface LodgeJobInput {
  name: string;
  suburbIds: string[];
  suburbListIds: string[];
  adapterIds: string[];
  runType: "immediate" | "scheduled";
  cronExpression?: string;
  config: CrawlJobConfig;
  adapterConfigs: Record<string, Record<string, unknown>>;
  debugPauseOnSubmit: boolean;
}

export function toLodgeJobInput(result: JobConfigFromPromptResult): LodgeJobInput {
  return {
    name: result.jobName,
    suburbIds: result.suburbs.map((s) => s.id),
    suburbListIds: result.suburbLists.map((l) => l.id),
    adapterIds: result.adapters.map((a) => a.id),
    runType: result.runType,
    cronExpression: result.cronExpression ?? undefined,
    config: result.globalConfig,
    adapterConfigs: Object.fromEntries(result.adapterOverrides.map((o) => [o.adapterId, o.config])),
    debugPauseOnSubmit: false,
  };
}

export function canConfirm(result: JobConfigFromPromptResult, defaultsAccepted: boolean): boolean {
  return (
    result.unresolved.length === 0 &&
    (defaultsAccepted || result.assumedDefaults.length === 0) &&
    result.adapters.length > 0 &&
    result.suburbs.length + result.suburbLists.length > 0
  );
}

const FIELD_LABELS: Record<keyof JobCrawlConfig, string> = {
  listingType: "Listing type",
  propertyTypes: "Property types",
  dateFilterMode: "Date mode",
  timeRange: "Time range",
  dateFrom: "From",
  dateTo: "To",
  minPrice: "Min price",
  maxPrice: "Max price",
  minBedrooms: "Min bedrooms",
  maxBedrooms: "Max bedrooms",
  minBathrooms: "Min bathrooms",
  minParking: "Min parking",
  excludeSurroundingSuburbs: "Exclude surrounding suburbs",
  excludePriceWithheld: "Exclude price-withheld sales",
  providerMode: "Provider mode",
  provider: "Provider",
};

const optionLabel = (options: readonly { value: string; label: string }[], value: string) =>
  options.find((o) => o.value === value)?.label ?? value;

function formatValue(field: keyof JobCrawlConfig, value: unknown): string {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (field === "listingType") return optionLabel(GLOBAL_LISTING_TYPE_OPTIONS, String(value));
  if (field === "timeRange") return optionLabel(GLOBAL_TIME_RANGE_OPTIONS, String(value));
  if (field === "propertyTypes" && Array.isArray(value)) {
    return value.map((v) => optionLabel(GLOBAL_PROPERTY_TYPE_OPTIONS, v)).join(", ");
  }
  if ((field === "minPrice" || field === "maxPrice") && typeof value === "number") {
    return `$${value.toLocaleString("en-AU")}`;
  }
  return Array.isArray(value) ? value.join(", ") : String(value);
}

/** Strip a "globalConfig." style prefix so model field names match config keys. */
export const defaultFieldKey = (field: string) => field.split(".").pop() ?? field;

export function findAssumedDefault(result: JobConfigFromPromptResult, field: string) {
  return result.assumedDefaults.find((d) => defaultFieldKey(d.field) === field);
}

export interface ConfigRow {
  field: string;
  label: string;
  value: string;
  source: "explicit" | "default";
  reason?: string;
}

export function describeConfigRows(result: JobConfigFromPromptResult): ConfigRow[] {
  return (Object.keys(FIELD_LABELS) as (keyof JobCrawlConfig)[])
    .filter((field) => result.globalConfig[field] !== undefined)
    .map((field) => {
      const assumed = findAssumedDefault(result, field);
      return {
        field,
        label: FIELD_LABELS[field],
        value: formatValue(field, result.globalConfig[field]),
        source: assumed ? "default" : "explicit",
        reason: assumed?.reason,
      };
    });
}

export function describeOverride(config: JobCrawlConfig): string {
  return (Object.keys(config) as (keyof JobCrawlConfig)[])
    .map((field) => `${FIELD_LABELS[field]}: ${formatValue(field, config[field])}`)
    .join(" · ");
}
